import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CloseIcon from "@assets/icons/close.svg";
import SearchIcon from "@assets/icons/search.svg";

import {
  DEFAULT_LIMIT_TAGS,
  MAX_LIMIT_TAGS,
  useSearchTags,
} from "../Sections/TagsSection/hooks/useSearchTags";
import { useSourcingSettings } from "@components/SourcingThemes/hooks/useSourcingSettings";
import { useSwitches } from "@hooks/useSwitches";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { onFocusWithoutScroll } from "@utils/misc.util";
import { filterMeetings } from "@utils/meeting.util";
import { cn } from "@utils/tailwind.util";
import {
  DESIRED_ORDER,
  INITIAL_TAGS_VALUE,
  CUSTOM_SECTION_TITLES,
  TagsTypes,
} from "@components/Sections/TagsSection/constants";
import { RelationObjectType } from "@enums/relations.enum";
import { ICompanyTag, IOptionsForTags } from "@interfaces/tag.interface";
import { SuggestionItem } from "./SuggestionItems/SuggestionItem";
import { NewEntityOptions } from "../AddNewEntity";
import { Checkbox } from "@ui/FormElements";
import { Shortcuts } from "@ui/Shortcuts/Shortcuts";
import { Spinner } from "@ui/Loaders/Spinner";
import { View } from "@ui/View";

interface Props {
  query: string;
  onChange?: (value: string) => void;
  onSelect: any;
  contentType?: string | RelationObjectType;
  keyForTypes: keyof typeof TagsTypes;
  placeholder?: string;
  className?: string;
  heightClassName?: string;
  uppercaseLabels?: boolean;
  isCompact?: boolean;
  excludedIds?: string[];
  /** Narrows `keyForTypes` for fields whose allowed types are configured per instance. */
  tagTypes?: string[];
  /** Items per group before the user types; typing switches to MAX_LIMIT_TAGS. */
  recentLimit?: number;
  /** Already-picked ids; with `showSelectedCheck` they render ticked. */
  selectedIds?: string[];
  /** Checkbox per row. Off by default. */
  showSelectedCheck?: boolean;
  showShortcuts?: boolean;
  showNewEntityOptions?: boolean;
  isInputHidden?: boolean;
}

export const TaggingComponent = ({
  query,
  onChange,
  onSelect,
  contentType = "",
  keyForTypes,
  placeholder = "Search",
  className,
  heightClassName,
  uppercaseLabels,
  isCompact,
  excludedIds,
  tagTypes,
  recentLimit = DEFAULT_LIMIT_TAGS,
  selectedIds,
  showSelectedCheck = false,
  showShortcuts = false,
  showNewEntityOptions = false,
  isInputHidden = false,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: sourcingSettings } = useSourcingSettings();

  const shouldShowLevel0Theme = sourcingSettings?.zero_level_count !== 1;

  const { data: items = INITIAL_TAGS_VALUE, keepTypingData } = useSearchTags(
    query,
    query.length > 0 ? MAX_LIMIT_TAGS : recentLimit,
    contentType,
    keyForTypes,
    excludedIds,
    tagTypes
  );

  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  itemRefs.current = [];

  const enableCompaniesAndPeople = true;
  const { isAvailable } = useSwitches();
  const enableFundsAndOpportunities = isAvailable("FUNDS_AND_OPPORTUNITIES");

  const finalHeightClassName = heightClassName ?? (isCompact ? "max-h-[240px]" : "");

  const groups = useMemo(() => {
    return (
      DESIRED_ORDER.filter(
        (group) => enableCompaniesAndPeople || (group !== "companies" && group !== "people")
      )
        .filter(
          (group) => enableFundsAndOpportunities || (group !== "funds" && group !== "opportunities")
        )
        // Enforced here too: `items` falls back to INITIAL_TAGS_VALUE, which has every group.
        .filter((group) => !tagTypes || tagTypes.includes(group))
        .filter((group) => group in items)
    );
  }, [enableCompaniesAndPeople, enableFundsAndOpportunities, items, tagTypes]);

  const initialVisibleCount = useMemo(
    () =>
      groups.reduce<Record<string, number>>((acc, group) => {
        acc[group] = 5;
        return acc;
      }, {}),
    [groups]
  );

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);

  const areAllValuesEmpty = Object.values(items).every(
    (value) => Array.isArray(value) && value.length === 0
  );

  let posinset = 0;

  const getFilteredItems = useCallback(
    (group: keyof IOptionsForTags) => {
      const itemsForGroup = items[group];

      // Special handling for meetings when limit is 15 and there are more than 5 meetings
      // But only if visibleCount is still at initial value (5)
      if (
        group === "meetings" &&
        query.length > 0 &&
        itemsForGroup.length > 5 &&
        visibleCount[group] === 5
      ) {
        return filterMeetings(itemsForGroup);
      }

      const result = query.length > 0 ? itemsForGroup.slice(0, visibleCount[group]) : itemsForGroup;

      if (group === "themes") {
        // filter theme with level 0 if there is only one theme with level 0
        return shouldShowLevel0Theme ? result : result.filter((t) => t.level !== 0);
      }

      return result;
    },
    [items, query.length, visibleCount, shouldShowLevel0Theme]
  );

  const showMore = useCallback(
    (group: keyof IOptionsForTags, count: number) =>
      setVisibleCount((prevValue) => ({ ...prevValue, [group]: prevValue[group] + count })),
    []
  );

  const clearQuery = () => onChange?.("");

  const flatItems = useMemo(() => {
    return groups.flatMap((group) => getFilteredItems(group).map((item) => ({ item, group })));
  }, [groups, getFilteredItems]);

  const existingCompanies = useMemo(() => {
    return flatItems.filter(
      (item) => item.item.object_type === "companies.company"
    ) as ICompanyTag[];
  }, [flatItems]);

  useEffect(() => {
    setVisibleCount(initialVisibleCount);
  }, [query]);

  useKeyboardNavigation({ flatItems, activeIndex, setActiveIndex, onSelect, clearQuery });

  useEffect(() => {
    if (activeIndex !== null && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    // Focus the input when component mounts and input is not hidden
    if (!isInputHidden && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [isInputHidden]);

  const isShowLoader = areAllValuesEmpty && !query.length;
  const isShowNewEntityOptions = showNewEntityOptions && !!contentType && !isShowLoader;

  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col divide-y divide-stroke-primary dark:divide-gray-600 non-selectable",
        className
      )}
    >
      <View.Condition if={!isInputHidden}>
        <div className="flex items-center gap-1 px-3">
          <SearchIcon width={20} height={20} className="text-content-secondary dark:text-white" />

          <input
            ref={inputRef}
            value={query}
            autoComplete="off"
            aria-owns="options"
            aria-controls="options"
            aria-autocomplete="list"
            aria-expanded="true"
            autoCapitalize="off"
            role="combobox"
            className="peer bg-white dark:bg-gray-850 dark:text-white w-full appearance-none border-none pr-6 py-3 text-sm text-content-primary outline-none placeholder:text-content-disabled focus:ring-0"
            onChange={(ev) => onChange?.(ev.target.value)}
            placeholder={placeholder}
            onFocus={(event) => onFocusWithoutScroll(event, inputRef)}
          />

          {query && (
            <button
              type="button"
              className="absolute h-4 p-0 top-2 right-4 text-content-secondary hover:text-content-primary dark:hover:text-invertedPrimary80"
              onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
              onClick={clearQuery}
              aria-label="Clear search"
            >
              <CloseIcon
                width={16}
                height={16}
                className="inline-block align-middle dark:text-white"
              />
            </button>
          )}
        </div>
      </View.Condition>

      <div
        role="listbox"
        id="options"
        tabIndex={-1}
        className={cn(finalHeightClassName, "flex-1 overflow-y-auto !outline-none")}
      >
        <View.Condition if={groups.length > 0}>
          {groups.map((group) => {
            const filteredItems = getFilteredItems(group);
            if (filteredItems.length === 0) return null;

            // Loaded but not shown. Caps at 10: response is trimmed to 15, first 5 are shown.
            const hiddenCount = items[group].length - filteredItems.length;
            const isShowMore = hiddenCount > 0;

            return (
              <div
                key={group}
                className="px-1 pb-1 pt-3 border-b dark:border-gray-600"
                onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
              >
                <div
                  className={cn(
                    "px-2 pb-1.5 text-xs font-medium text-content-secondary dark:text-white",
                    { uppercase: uppercaseLabels }
                  )}
                >
                  {CUSTOM_SECTION_TITLES[group] ?? group}
                </div>

                <ul className="space-y-1">
                  {filteredItems.map((item) => {
                    const position = posinset++;
                    const isActive = flatItems[activeIndex ?? -1]?.item === item;

                    return (
                      <li
                        key={"id" in item ? item.id : ""}
                        ref={(el) => {
                          itemRefs.current[position] = el;
                        }}
                        role="option"
                        id={`option-${position}`}
                        aria-posinset={position}
                        aria-setsize={filteredItems.length}
                        data-value={"id" in item ? item.id : ""}
                        aria-selected={isActive}
                        className="hover:bg-background-secondary dark:hover:bg-gray-700 aria-selected:bg-background-secondary dark:aria-selected:bg-gray-600 rounded-md"
                        tabIndex={-1}
                        onClick={() => onSelect(item, group)}
                        onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
                      >
                        {showSelectedCheck ? (
                          <div className="flex min-w-0 items-center gap-1 px-1">
                            <Checkbox
                              parentClassName="flex-shrink-0"
                              className="mr-1 cursor-pointer border-stroke-primary bg-transparent"
                              isChecked={!!selectedIds?.includes("id" in item ? item.id : "")}
                              variant="small"
                              readOnly
                              tabIndex={-1}
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1">
                              <SuggestionItem item={item} group={group} />
                            </div>
                          </div>
                        ) : (
                          <SuggestionItem item={item} group={group} />
                        )}
                      </li>
                    );
                  })}
                </ul>
                {isShowMore ? (
                  <div
                    className={cn(
                      "w-fit cursor-pointer p-1 mx-1 text-sm font-medium text-gray-450 transition-all ease-linear hover:text-content-primar",
                      "dark:text-invertedPrimary80 dark:hover:text-gray-500"
                    )}
                    onClick={() => showMore(group, hiddenCount)}
                    onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
                  >
                    Show {hiddenCount} More
                  </div>
                ) : null}
                {/* Everything loaded is shown, but the server had more — narrow the query. */}
                {!isShowMore && keepTypingData[group] ? (
                  <div className="py-1 text-[13px] text-[#9CA1AF]">
                    Keep typing to see more results
                  </div>
                ) : null}
              </div>
            );
          })}
        </View.Condition>

        <View.Condition if={isShowLoader}>
          <div className="h-[200px]">
            <Spinner />
          </div>
        </View.Condition>

        <View.Condition if={isShowNewEntityOptions}>
          <NewEntityOptions
            contentType={contentType as RelationObjectType}
            searchQuery={query}
            clearQuery={clearQuery}
            onSelect={onSelect}
            existingCompanies={existingCompanies}
            uppercaseLabels={uppercaseLabels}
          />
        </View.Condition>
      </div>
      {showShortcuts && <Shortcuts action="Search" />}
    </div>
  );
};
